import theme from '../../theme';
import { useState } from 'react';
import PageHeader from '../PageHeader';

// ── CSS ────────────────────────────────────────────────────────────────────────

const CSS = `
  .garrison-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 80%;
    height: 4px;
    border-radius: 2px;
    background: rgba(184, 149, 90, 0.3);
    outline: none;
    cursor: pointer;
    display: block;
    margin: 0 auto;
  }
  .garrison-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${theme.gold};
    cursor: pointer;
    border: 3px solid ${theme.bgDark};
  }
  .garrison-slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${theme.gold};
    cursor: pointer;
    border: 3px solid ${theme.bgDark};
  }
  .garrison-mobile-connector {
    display: none;
  }
  @media (max-width: 700px) {
    .garrison-panels {
      flex-direction: column !important;
      gap: 0 !important;
    }
    .garrison-divider {
      display: none !important;
    }
    .garrison-mobile-connector {
      display: flex !important;
      justify-content: center;
      align-items: center;
      gap: 8px;
      padding: 12px 0;
    }
  }
`;

function GarrisonTriangle() {
  const [timePreference, setTimePreference] = useState(5);

  const minBase = 100, maxBase = 380;
  const minHeight = 60, maxHeight = 180;

  const base1 = minBase + (maxBase - minBase) * (9 - timePreference) / 8;
  const height1 = minHeight + (maxHeight - minHeight) * (timePreference - 1) / 8;

  const investmentRatio = (base1 - minBase) / (maxBase - minBase);
  const base2 = minBase + (maxBase - minBase) * investmentRatio * 1.2;
  const height2 = minHeight + (maxHeight - minHeight) * investmentRatio * 1.3;

  const rateLabel = timePreference <= 3 ? 'Low' : timePreference <= 6 ? 'Medium' : 'High';

  const VW = 520, VH = 300;
  const RX = VW - 40, RY = VH - 30;

  const drawSVG = (base, height, isPhase1) => {
    const LX = Math.max(10, RX - base), LY = RY;
    const TX = RX, TY = RY - height;
    const hmx = (LX + TX) / 2;
    const hmy = (LY + TY) / 2;
    const angle = Math.atan2(TY - LY, TX - LX) * 180 / Math.PI;

    const fillColor = isPhase1 ? '#4a7fc1' : theme.success;
    const strokeColor = isPhase1 ? '#2a5090' : '#1d7a52';

    // Badge dimensions for hypotenuse label
    const badgeW = 110, badgeH = 18, badgeRx = 4;
    const badgeCx = hmx - badgeW / 2;
    const badgeCy = hmy - badgeH / 2 - 10;

    return (
      <svg width="100%" viewBox={`0 0 ${VW} ${VH}`}>
        {/* Triangle */}
        <polygon
          points={`${LX},${LY} ${TX},${TY} ${RX},${RY}`}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth="1.5"
        />

        {/* Base label */}
        <text x={(LX + RX) / 2} y={LY + 20} textAnchor="middle" fontSize="11" fill={theme.textMuted}>
          Production Length
        </text>

        {/* Right side label */}
        <text
          x={RX + 16}
          y={(TY + RY) / 2}
          textAnchor="middle"
          fontSize="11"
          fill={theme.textMuted}
          transform={`rotate(90, ${RX + 16}, ${(TY + RY) / 2})`}
        >
          Consumable Output
        </text>

        {/* Hypotenuse badge (phase 1 only) */}
        {isPhase1 && (
          <g transform={`rotate(${angle}, ${hmx}, ${hmy - 10})`}>
            <rect
              x={badgeCx}
              y={badgeCy}
              width={badgeW}
              height={badgeH}
              rx={badgeRx}
              fill={theme.gold}
            />
            <text
              x={hmx}
              y={badgeCy + badgeH / 2 + 4}
              textAnchor="middle"
              fontSize="10"
              fill={theme.bgDark}
              fontWeight="600"
            >
              Interest Rate: {rateLabel}
            </text>
          </g>
        )}
      </svg>
    );
  };

  const phaseDescStyle = {
    marginTop: '0.75rem',
    textAlign: 'center',
    background: theme.bgCard,
    border: '1px solid rgba(184, 149, 90, 0.3)',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '13px',
    color: theme.textPrimary,
    lineHeight: '1.5',
  };

  return (
    <div className="page-container">
      <style>{CSS}</style>

      <PageHeader
        title="The Hayekian-Garrison Triangle"
        description="Capital takes time to build. Time preference determines how much of it you invest, and how much you produce tomorrow."
      />

      <div className="garrison-panels" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>

        {/* Phase 1 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ textAlign: 'center', fontSize: '14px', color: theme.textPrimary, marginBottom: '0.5rem' }}>
            Initial Phase
          </h3>
          {drawSVG(base1, height1, true)}

          {/* Slider */}
          <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
            <input
              type="range"
              min="1"
              max="9"
              value={timePreference}
              onChange={(e) => setTimePreference(Number(e.target.value))}
              className="garrison-slider"
              aria-label="Time Preference"
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '80%',
              margin: '4px auto 0',
              fontSize: '12px',
              color: theme.textMuted,
            }}>
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
            </div>
            <div style={{ fontSize: '13px', color: theme.textPrimary, marginTop: '0.3rem' }}>
              Time Preference: <strong style={{ color: theme.gold }}>{rateLabel}</strong>
            </div>
          </div>

          <div style={phaseDescStyle}>
            {timePreference <= 3
              ? 'Low time preference → sacrifice now → invest in longer production'
              : timePreference <= 6
              ? 'Moderate time preference → balanced saving and consumption'
              : 'High time preference → consume now → little investment'}
          </div>

          <p style={{ fontSize: '13px', color: theme.textMuted, fontStyle: 'italic', textAlign: 'center', marginTop: '8px' }}>
            Low time preference. Longer production structure. The businesses worth owning reinvest deeply and accept deferred payoff.
          </p>
        </div>

        {/* Desktop phase connector */}
        <div className="garrison-divider" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0 4px',
          alignSelf: 'stretch',
        }}>
          <div style={{ width: '1px', background: 'rgba(184, 149, 90, 0.2)', flex: 1 }} />
          <div style={{
            padding: '10px 2px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}>
            <span style={{ fontSize: '18px', color: theme.gold }}>→</span>
            <span style={{
              fontSize: '12px',
              color: theme.textMuted,
              writingMode: 'vertical-rl',
              fontStyle: 'italic',
              letterSpacing: '0.05em',
            }}>
              investment compounds into...
            </span>
          </div>
          <div style={{ width: '1px', background: 'rgba(184, 149, 90, 0.2)', flex: 1 }} />
        </div>

        {/* Mobile phase connector */}
        <div className="garrison-mobile-connector">
          <span style={{ fontSize: '20px', color: theme.gold }}>↓</span>
          <span style={{ fontSize: '12px', color: theme.textMuted, fontStyle: 'italic' }}>
            investment compounds into...
          </span>
        </div>

        {/* Phase 2 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ textAlign: 'center', fontSize: '14px', color: theme.textPrimary, marginBottom: '0.5rem' }}>
            Later Phase
          </h3>
          {drawSVG(base2, height2, false)}

          <div style={{ height: '64px' }} />

          <div style={phaseDescStyle}>
            {timePreference <= 3
              ? 'High investment → expanded output and production capacity'
              : timePreference <= 6
              ? 'Moderate investment → modest future growth'
              : 'Low investment → contracted output and production capacity'}
          </div>

          <p style={{ fontSize: '13px', color: theme.textMuted, fontStyle: 'italic', textAlign: 'center', marginTop: '8px' }}>
            The consequence of Phase 1. Patient capital compounds. Present sacrifice yields future abundance.
          </p>
        </div>

      </div>
    </div>
  );
}

export default GarrisonTriangle;
